import { DATE_FORMATS, parseFlexibleDate } from '../../../../../../utils/dateUtils';
import { BankTransaction } from './types';

export interface MonthGroup {
    monthKey: string; // sortable "YYYY-MM"; '' for unparseable dates
    monthLabel: string; // "Jan 2026"; 'Undated' for unparseable dates — used in subtotal rows
    tabLabel: string; // "Jan 26" (MMM YY); 'Undated' for unparseable dates — used in tab buttons
    transactions: BankTransaction[];
    credits: number;
    debits: number;
}

export interface GroupedTransactions {
    groups: MonthGroup[];
    grandCredits: number;
    grandDebits: number;
    grandNet: number;
}

function toNumber(value: number | string): number {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : 0;
}

function creditOf(txn: BankTransaction): number {
    return txn.transactionType === 'debit' ? 0 : Math.abs(toNumber(txn.amount));
}

function debitOf(txn: BankTransaction): number {
    return txn.transactionType === 'debit' ? Math.abs(toNumber(txn.amount)) : 0;
}

export function groupTransactionsByMonth(transactions: BankTransaction[]): GroupedTransactions {
    const byMonth = new Map<string, MonthGroup>();
    let grandCredits = 0;
    let grandDebits = 0;

    for (const txn of transactions) {
        const parsed = parseFlexibleDate(txn.date);
        const monthKey = parsed ? parsed.format('YYYY-MM') : '';
        const monthLabel = parsed ? parsed.format(DATE_FORMATS.MONTH_SHORT_YEAR) : 'Undated';
        const tabLabel = parsed ? parsed.format('MMM YY') : 'Undated';

        let group = byMonth.get(monthKey);
        if (!group) {
            group = { monthKey, monthLabel, tabLabel, transactions: [], credits: 0, debits: 0 };
            byMonth.set(monthKey, group);
        }
        const credit = creditOf(txn);
        const debit = debitOf(txn);
        group.transactions.push(txn);
        group.credits += credit;
        group.debits += debit;
        grandCredits += credit;
        grandDebits += debit;
    }

    const groups = [...byMonth.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    return { groups, grandCredits, grandDebits, grandNet: grandCredits - grandDebits };
}
